import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { FloatingWhatsApp } from "@/components/FloatingWhatsApp";
import { AccessibilityButton } from "@/components/AccessibilityButton";
import {
  HeroSection,
  ConsultorioCard,
  OnlineConsultation,
  StatisticsSection,
} from "@/components/Consultorios";
import { consultorios as defaultConsultorios } from "@/data/consultorios";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { PageUnavailable } from "@/components/ui/PageUnavailable";

function parseJson<T>(raw: string | undefined, fallback: T): T {
  if (!raw) return fallback;
  try { return { ...fallback, ...JSON.parse(raw) } as T; } catch { return fallback; }
}

function parseJsonArray<T>(raw: string | undefined, fallback: T[]): T[] {
  if (!raw) return fallback;
  try {
    const p = JSON.parse(raw);
    return Array.isArray(p) && p.length > 0 ? p : fallback;
  } catch { return fallback; }
}

export default function Consultorios() {
  const { getSetting } = useSiteSettings();

  const visible = getSetting('page_consultorios_visible', 'true');
  if (visible === 'false') return <PageUnavailable />;

  const hero = parseJson(getSetting('consultorios_hero'), {
    label: '3 Localizações em São Paulo',
    labelIcon: 'MapPin',
    title: 'Nossos Consultórios',
    subtitle: 'Atendimento de qualidade em locais estratégicos. Escolha o consultório mais próximo de você.',
    badges: [
      { label: 'Paulista · Mogi · Suzano', icon: 'MapPin' },
      { label: '5.0 no Google',            icon: 'Star' },
      { label: 'CRN/3-63492',              icon: 'Shield' },
    ],
  });

  const consultoriosData = parseJsonArray(
    getSetting('consultorios_data'),
    defaultConsultorios as any[],
  );

  const online = parseJson(getSetting('consultorios_online'), {
    headerIcon: 'Monitor',
    title: 'Consulta Online',
    desc: 'Não pode se deslocar? Atendimento de qualidade onde você estiver, com a mesma dedicação da consulta presencial.',
    buttonText: 'Agendar Online',
    buttonUrl: '',
    features: [
      { icon: 'Clock',    title: 'Horários Flexíveis', desc: 'Agendamento conforme sua disponibilidade, inclusive noturno e fins de semana.' },
      { icon: 'Home',     title: 'Conforto de Casa',   desc: 'Atendimento completo sem sair de onde você estiver, com total privacidade.' },
      { icon: 'Sparkles', title: 'Mesma Qualidade',    desc: 'Consulta personalizada com o mesmo cuidado e atenção do atendimento presencial.' },
    ],
  });

  const stats = parseJson(getSetting('consultorios_stats'), {
    sectionTitle: 'Números que Falam por Si',
    sectionSubtitle: 'A confiança dos nossos pacientes em números',
    stats: [
      { value: '25+', label: 'Avaliações Google' },
      { value: '5.0', label: 'Média de Estrelas' },
      { value: '3',   label: 'Localizações' },
      { value: '50+', label: 'Pacientes Atendidos' },
    ],
  });

  const handleCall = (phone: string) => {
    window.open(`tel:${phone.replace(/\D/g, '')}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="pt-24">
        <HeroSection
          label={hero.label}
          labelIcon={(hero as any).labelIcon}
          title={hero.title}
          subtitle={hero.subtitle}
          badges={hero.badges}
        />

        {/* Consultórios */}
        <section className="py-14">
          <div className="container mx-auto px-6 max-w-5xl">
            <div className="space-y-10">
              {consultoriosData.map((consultorio: any, index: number) => (
                <ConsultorioCard
                  key={consultorio.id}
                  consultorio={consultorio}
                  index={index}
                  onCall={handleCall}
                />
              ))}
            </div>
          </div>
        </section>

        <OnlineConsultation
          headerIcon={(online as any).headerIcon}
          title={online.title}
          desc={online.desc}
          buttonText={online.buttonText}
          buttonUrl={online.buttonUrl}
          features={online.features}
        />

        <StatisticsSection
          sectionTitle={stats.sectionTitle}
          sectionSubtitle={stats.sectionSubtitle}
          stats={stats.stats}
        />
      </div>

      <Footer />
      <FloatingWhatsApp />
      <AccessibilityButton />
    </div>
  );
}
